import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { UserMinus, UserPlus, ShieldCheck, Warning, EnvelopeSimple, X, CheckCircle, Copy, ClockCounterClockwise, PaperPlaneTilt, Trash, PencilSimple, Key, ChartLineUp } from "@phosphor-icons/react";
import { ScorecardDrawer } from "@/pages/Insights";

export default function TeamAdmin() {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [invites, setInvites] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [busyToken, setBusyToken] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [scorecardUid, setScorecardUid] = useState("");
  const [flashMsg, setFlashMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, inv] = await Promise.all([
        api.get("/team"),
        api.get("/invites/pending").catch(() => ({ data: [] })),
      ]);
      setTeam(t.data);
      setInvites(inv.data || []);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (user && user !== false && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  const toggleSuspend = async (u) => {
    setBusyId(u.id);
    try {
      const endpoint = u.suspended ? "unsuspend" : "suspend";
      await api.post(`/users/${u.id}/${endpoint}`);
      await load();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBusyId("");
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name || u.email} permanently?\n\nAny candidates they own will be reassigned to you. This cannot be undone.`)) return;
    setBusyId(u.id);
    setError("");
    try {
      const { data } = await api.delete(`/users/${u.id}`);
      const reassigned = data?.reassigned_candidates || 0;
      const suffix = reassigned > 0 ? ` · ${reassigned} candidate${reassigned === 1 ? "" : "s"} reassigned to you` : "";
      setFlashMsg(`Deleted ${u.email}${suffix}`);
      setTimeout(() => setFlashMsg(""), 5000);
      await load();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBusyId("");
    }
  };

  const sendPasswordReset = async (u) => {
    if (!window.confirm(`Send a password reset email to ${u.email}? The link expires in 1 hour.`)) return;
    setBusyId(u.id);
    setError("");
    try {
      const { data } = await api.post(`/users/${u.id}/send-password-reset`);
      setFlashMsg(
        data?.email_sent
          ? `Reset email sent to ${u.email}`
          : `Reset link created — email delivery failed. Copy the link from the backend logs.`
      );
      setTimeout(() => setFlashMsg(""), 5000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="space-y-8" data-testid="team-admin-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-1.5">
            <ShieldCheck size={12} weight="bold" /> Admin
          </div>
          <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl leading-none">
            Team members
          </h1>
          <div className="text-sm text-zinc-500 mt-3">
            Invite recruiters, promote to admin, or suspend anyone who leaves. Suspended users can't log in or be assigned to candidates.
          </div>
        </div>
        <button
          onClick={() => setShowInvite(true)}
          data-testid="invite-user-btn"
          className="flex items-center gap-2 bg-zinc-950 text-white px-5 py-3 rounded-sm hover:bg-zinc-800 transition-colors text-sm font-medium"
        >
          <EnvelopeSimple size={16} weight="bold" /> Invite user
        </button>
      </div>

      {error && (
        <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] px-4 py-3 rounded-sm text-sm flex items-center gap-2">
          <Warning size={14} weight="bold" /> {error}
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        {flashMsg && (
          <div className="mx-6 mt-3 border border-emerald-500 bg-emerald-50 text-emerald-900 px-3 py-2 text-xs rounded-sm flex items-center gap-2" data-testid="team-flash">
            <CheckCircle size={12} weight="fill" /> {flashMsg}
          </div>
        )}
        <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-4 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="p-6 text-zinc-500 text-sm uppercase tracking-[0.2em]">Loading</div>
        ) : team.length === 0 ? (
          <div className="p-6 text-zinc-500 text-sm">No users found.</div>
        ) : (
          team.map((u) => {
            const isSelf = u.id === user?.id;
            return (
              <div
                key={u.id}
                className={`grid grid-cols-12 px-6 py-4 border-b border-zinc-100 last:border-0 items-center ${u.suspended ? "bg-zinc-50" : ""}`}
                data-testid={`team-row-${u.id}`}
              >
                <div className="col-span-3">
                  <div className={`font-medium ${u.suspended ? "text-zinc-400 line-through" : "text-zinc-950"}`}>
                    {u.name || "—"}
                  </div>
                  {isSelf && <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-0.5">You</div>}
                </div>
                <div className="col-span-3 text-sm text-zinc-600 truncate">{u.email}</div>
                <div className="col-span-2">
                  <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] rounded-sm ${
                    u.role === "admin"
                      ? "bg-zinc-950 text-white"
                      : "border border-zinc-200 text-zinc-600"
                  }`}>
                    {u.role || "user"}
                  </span>
                </div>
                <div className="col-span-4 flex items-center gap-1.5 justify-end flex-wrap">
                  <button
                    onClick={() => setScorecardUid(u.id)}
                    data-testid={`scorecard-${u.id}`}
                    className="inline-flex items-center gap-1 text-[11px] border border-zinc-200 bg-white text-zinc-950 px-2.5 py-1.5 rounded-sm hover:bg-zinc-100"
                    title="View scorecard"
                  >
                    <ChartLineUp size={11} weight="bold" /> Scorecard
                  </button>
                  <button
                    onClick={() => setEditingUser(u)}
                    disabled={busyId === u.id}
                    data-testid={`edit-user-${u.id}`}
                    className="inline-flex items-center gap-1 text-[11px] border border-zinc-200 bg-white text-zinc-950 px-2.5 py-1.5 rounded-sm hover:bg-zinc-100 disabled:opacity-50"
                  >
                    <PencilSimple size={11} weight="bold" /> Edit
                  </button>
                  {isSelf ? (
                    <span className="text-xs text-zinc-400 px-2">Your account</span>
                  ) : (
                    <>
                      <button
                        onClick={() => sendPasswordReset(u)}
                        disabled={busyId === u.id}
                        data-testid={`send-reset-${u.id}`}
                        className="inline-flex items-center gap-1 text-[11px] border border-zinc-200 bg-white text-zinc-950 px-2.5 py-1.5 rounded-sm hover:bg-zinc-100 disabled:opacity-50"
                        title="Email a password-reset link"
                      >
                        <Key size={11} weight="bold" /> Send reset
                      </button>
                      {u.suspended ? (
                        <button
                          onClick={() => toggleSuspend(u)}
                          disabled={busyId === u.id}
                          data-testid={`unsuspend-${u.id}`}
                          className="inline-flex items-center gap-1 text-[11px] bg-emerald-600 text-white px-2.5 py-1.5 rounded-sm hover:bg-emerald-700 disabled:opacity-50"
                        >
                          <UserPlus size={11} weight="bold" /> Reactivate
                        </button>
                      ) : (
                        <button
                          onClick={() => toggleSuspend(u)}
                          disabled={busyId === u.id}
                          data-testid={`suspend-${u.id}`}
                          className="inline-flex items-center gap-1 text-[11px] border border-zinc-300 text-zinc-700 px-2.5 py-1.5 rounded-sm hover:bg-zinc-100 disabled:opacity-50"
                        >
                          <UserMinus size={11} weight="bold" /> Suspend
                        </button>
                      )}
                      <button
                        onClick={() => deleteUser(u)}
                        disabled={busyId === u.id}
                        data-testid={`delete-user-${u.id}`}
                        className="inline-flex items-center gap-1 text-[11px] border border-[#FF2A2A] text-[#B91C1C] px-2.5 py-1.5 rounded-sm hover:bg-red-50 disabled:opacity-50"
                      >
                        <Trash size={11} weight="bold" /> Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="text-xs text-zinc-500">
        <b>Note:</b> Invited users get a one-click accept link via email that expires in 7 days. If email delivery fails you can copy the link from the confirmation dialog and share it manually.
      </div>

      {invites.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <ClockCounterClockwise size={12} weight="bold" className="text-zinc-500" />
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Pending invites · {invites.length}</div>
          </div>
          {flashMsg && (
            <div className="mb-3 border border-emerald-500 bg-emerald-50 text-emerald-900 px-3 py-2 text-xs rounded-sm flex items-center gap-2" data-testid="invite-flash">
              <CheckCircle size={12} weight="fill" /> {flashMsg}
            </div>
          )}
          <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
            {invites.map((inv) => {
              const expired = inv.expires_at ? (new Date(inv.expires_at) < new Date()) : false;
              return (
                <div key={inv.token} className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 last:border-0 items-center text-sm gap-2" data-testid={`invite-row-${inv.token.slice(0, 8)}`}>
                  <div className="col-span-3 font-medium truncate">{inv.name || "—"}</div>
                  <div className="col-span-4 text-zinc-500 truncate">{inv.email}</div>
                  <div className="col-span-2">
                    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] rounded-sm ${
                      inv.role === "admin" ? "bg-zinc-950 text-white" : "border border-zinc-200 text-zinc-600"
                    }`}>{inv.role}</span>
                  </div>
                  <div className="col-span-1 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                    {expired ? "expired" : "pending"}
                  </div>
                  <div className="col-span-2 flex items-center gap-1 justify-end">
                    <button
                      onClick={async () => {
                        setBusyToken(inv.token);
                        try {
                          const { data } = await api.post(`/invites/${inv.token}/resend`);
                          setFlashMsg(data.email_sent ? `Re-sent to ${inv.email}` : `Link refreshed — email delivery failed for ${inv.email}`);
                          setTimeout(() => setFlashMsg(""), 4000);
                          await load();
                        } catch (err) {
                          setError(getErrorMessage(err));
                        } finally { setBusyToken(""); }
                      }}
                      disabled={busyToken === inv.token}
                      data-testid={`resend-invite-${inv.token.slice(0, 8)}`}
                      className="inline-flex items-center gap-1 text-[11px] border border-zinc-200 bg-white text-zinc-950 px-2.5 py-1.5 rounded-sm hover:bg-zinc-100 disabled:opacity-50"
                    >
                      <PaperPlaneTilt size={11} weight="bold" /> Resend
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Cancel invite for ${inv.email}?`)) return;
                        setBusyToken(inv.token);
                        try {
                          await api.delete(`/invites/${inv.token}`);
                          setFlashMsg(`Cancelled invite for ${inv.email}`);
                          setTimeout(() => setFlashMsg(""), 3000);
                          await load();
                        } catch (err) {
                          setError(getErrorMessage(err));
                        } finally { setBusyToken(""); }
                      }}
                      disabled={busyToken === inv.token}
                      data-testid={`cancel-invite-${inv.token.slice(0, 8)}`}
                      className="inline-flex items-center gap-1 text-[11px] border border-[#FF2A2A] text-[#B91C1C] px-2.5 py-1.5 rounded-sm hover:bg-red-50 disabled:opacity-50"
                    >
                      <Trash size={11} weight="bold" /> Cancel
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showInvite && (
        <InviteModal onClose={() => setShowInvite(false)} onSent={() => { setShowInvite(false); load(); }} />
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          isSelf={editingUser.id === user?.id}
          onClose={() => setEditingUser(null)}
          onSaved={(msg) => {
            setEditingUser(null);
            if (msg) { setFlashMsg(msg); setTimeout(() => setFlashMsg(""), 3000); }
            load();
          }}
        />
      )}

      {scorecardUid && (
        <ScorecardDrawer uid={scorecardUid} period="month" onClose={() => setScorecardUid("")} />
      )}
    </div>
  );
}

function EditUserModal({ user, isSelf, onClose, onSaved }) {
  const [form, setForm] = useState({
    first_name: user.first_name || "",
    last_name: user.last_name || "",
    role: user.role || "user",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError("First name and last name are both required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/users/${user.id}`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        role: form.role,
      });
      onSaved(`Updated ${form.first_name.trim()} ${form.last_name.trim()}`);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/50 flex items-center justify-center p-4" data-testid="edit-user-modal">
      <div className="bg-white border border-zinc-200 rounded-sm w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Team</div>
            <div className="font-heading font-bold text-2xl tracking-tight">Edit user</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-950" data-testid="edit-user-close"><X size={20} weight="bold" /></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4" data-testid="edit-user-form">
          <div className="text-xs text-zinc-500">
            Email: <b className="text-zinc-950 break-all">{user.email}</b>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name" required>
              <input required value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="edit-first-name-input" className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950" autoFocus />
            </Field>
            <Field label="Last name" required>
              <input required value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="edit-last-name-input" className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950" />
            </Field>
          </div>
          <Field label="Role">
            <div className="flex gap-2">
              {[
                { v: "user", label: "User", desc: "Sees only their candidates" },
                { v: "admin", label: "Admin", desc: "Sees everything, manages team" },
              ].map((r) => {
                const disabled = isSelf && r.v !== "admin";
                return (
                  <button
                    key={r.v}
                    type="button"
                    onClick={() => !disabled && set("role", r.v)}
                    disabled={disabled}
                    data-testid={`edit-role-${r.v}`}
                    className={`flex-1 border rounded-sm px-3 py-2.5 text-left ${form.role === r.v ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white text-zinc-950 hover:border-zinc-400"} ${disabled ? "opacity-40 cursor-not-allowed hover:border-zinc-200" : ""}`}
                  >
                    <div className="text-sm font-medium">{r.label}</div>
                    <div className={`text-[10px] mt-0.5 ${form.role === r.v ? "text-zinc-300" : "text-zinc-500"}`}>{r.desc}</div>
                  </button>
                );
              })}
            </div>
            {isSelf && (
              <div className="text-[10px] text-zinc-400 mt-1">You can't demote your own admin role.</div>
            )}
          </Field>

          {error && (
            <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] text-sm px-3 py-2 rounded-sm" data-testid="edit-user-error">{error}</div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-zinc-200 bg-white text-zinc-950 py-3 rounded-sm font-medium hover:bg-zinc-100 text-sm">Cancel</button>
            <button type="submit" disabled={submitting || !form.first_name.trim() || !form.last_name.trim()} data-testid="save-user-btn" className="flex-1 bg-zinc-950 text-white py-3 rounded-sm font-medium hover:bg-zinc-800 text-sm disabled:opacity-50">
              {submitting ? "Saving..." : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function InviteModal({ onClose, onSent }) {
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", role: "user" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/users/invite", form);
      setResult(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.invite_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard may be blocked */ }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/50 flex items-center justify-center p-4" data-testid="invite-modal">
      <div className="bg-white border border-zinc-200 rounded-sm w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Team</div>
            <div className="font-heading font-bold text-2xl tracking-tight">Invite user</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-950"><X size={20} weight="bold" /></button>
        </div>

        {result ? (
          <div className="p-6 space-y-4">
            <div className="border border-emerald-500 bg-emerald-50 text-emerald-900 p-4 rounded-sm text-sm flex items-start gap-3" data-testid="invite-sent">
              <CheckCircle size={18} weight="fill" className="flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium mb-1">
                  {result.email_sent ? "Invite email sent" : "Invite created (email delivery failed)"}
                </div>
                <div className="text-xs">
                  {result.email_sent
                    ? `${result.email} will receive an email from onboarding@recruitflow.com.au with a 1-click link.`
                    : "Copy the link below and share it manually. The link expires in 7 days."}
                </div>
              </div>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Invite link (fallback)</label>
              <div className="flex gap-2">
                <input readOnly value={result.invite_link} data-testid="invite-link-value" className="flex-1 bg-zinc-50 border border-zinc-200 px-3 py-2 text-xs rounded-sm font-mono" onClick={(e) => e.target.select()} />
                <button onClick={copy} data-testid="copy-invite-link" className="flex items-center gap-1.5 border border-zinc-200 bg-white text-zinc-950 px-3 py-2 rounded-sm text-xs hover:bg-zinc-100">
                  <Copy size={12} weight="bold" /> {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
            <button onClick={onSent} className="w-full bg-zinc-950 text-white py-3 rounded-sm font-medium hover:bg-zinc-800 text-sm">
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4" data-testid="invite-form">
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name" required>
                <input required value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="invite-first-name-input" className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950" placeholder="Sarah" autoFocus />
              </Field>
              <Field label="Last name" required>
                <input required value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="invite-last-name-input" className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950" placeholder="Recruiter" />
              </Field>
            </div>
            <Field label="Email" required>
              <input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="invite-email-input" className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950" placeholder="sarah@growthtalentgroup.com.au" />
            </Field>
            <Field label="Role">
              <div className="flex gap-2">
                {[
                  { v: "user", label: "User", desc: "Sees only their candidates" },
                  { v: "admin", label: "Admin", desc: "Sees everything, manages team" },
                ].map((r) => (
                  <button
                    key={r.v}
                    type="button"
                    onClick={() => set("role", r.v)}
                    data-testid={`invite-role-${r.v}`}
                    className={`flex-1 border rounded-sm px-3 py-2.5 text-left ${form.role === r.v ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white text-zinc-950 hover:border-zinc-400"}`}
                  >
                    <div className="text-sm font-medium">{r.label}</div>
                    <div className={`text-[10px] mt-0.5 ${form.role === r.v ? "text-zinc-300" : "text-zinc-500"}`}>{r.desc}</div>
                  </button>
                ))}
              </div>
            </Field>

            {error && (
              <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] text-sm px-3 py-2 rounded-sm" data-testid="invite-error">{error}</div>
            )}

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 border border-zinc-200 bg-white text-zinc-950 py-3 rounded-sm font-medium hover:bg-zinc-100 text-sm">Cancel</button>
              <button type="submit" disabled={submitting || !form.email || !form.first_name || !form.last_name} data-testid="send-invite-btn" className="flex-1 bg-zinc-950 text-white py-3 rounded-sm font-medium hover:bg-zinc-800 text-sm disabled:opacity-50">
                {submitting ? "Sending..." : "Send invite"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, children, required }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">
      {label}{required && <span className="text-[#FF2A2A]"> *</span>}
    </label>
    {children}
  </div>
);
